import { useState } from 'react'
import { flushSync } from 'react-dom'
import type {
  CardStub,
  DashboardSection,
} from '@/features/dashboard/api/schemas'
import { tileButtonId, withViewTransition } from '@/shared/lib/viewTransition'
import { ExpandedModule } from './ExpandedModule'
import { ModulePill } from './ModulePill'
import { ModuleTile } from './ModuleTile'
import { SectionIcon } from './SectionIcon'
import styles from './SectionGroup.module.css'

type Expanded = { cardId: number; fromKeyboard: boolean }

type SectionGroupProps = {
  section: DashboardSection
  /** The deep dive a card opens from its expanded view, if any. */
  deepDiveOf: (stub: CardStub) => DashboardSection | undefined
}

/**
 * A section's tiles in a four-column grid. Clicking one expands it into a
 * panel beside them, narrowing the tiles to a one-tile-wide sidebar where the
 * expanded card's tile becomes a pill; clicking another tile switches the
 * expanded card and the panel's ✕ goes back to the grid. The change morphs
 * through a view transition where the browser supports one.
 */
export const SectionGroup = ({ section, deepDiveOf }: SectionGroupProps) => {
  const [expanded, setExpanded] = useState<Expanded | null>(null)
  const expandedStub = section.cards.find(
    card => card.cardId === expanded?.cardId
  )
  const headingId = `section-${section.id}`

  const expand = (cardId: number, fromKeyboard: boolean) =>
    withViewTransition(() =>
      flushSync(() => setExpanded({ cardId, fromKeyboard }))
    )

  const collapse = (fromKeyboard: boolean) => {
    const cardId = expanded?.cardId
    withViewTransition(() => {
      flushSync(() => setExpanded(null))
      // Keyboard users go back to the tile they opened.
      if (fromKeyboard && cardId !== undefined) {
        document.getElementById(tileButtonId(cardId))?.focus()
      }
    })
  }

  return (
    <div className={`${styles.group} ${expandedStub ? styles.expanded : ''}`}>
      <section className={styles.sidebar} aria-labelledby={headingId}>
        <h2 id={headingId} className={styles.heading}>
          <SectionIcon sectionId={section.id} className={styles.icon} />
          {section.title}
        </h2>
        <div className={styles.list}>
          {section.cards.map(stub =>
            stub.cardId === expandedStub?.cardId ? (
              <ModulePill key={stub.cardId} name={stub.name} />
            ) : (
              <ModuleTile
                key={stub.cardId}
                stub={stub}
                onExpand={fromKeyboard => expand(stub.cardId, fromKeyboard)}
              />
            )
          )}
        </div>
      </section>
      {expandedStub && (
        <div className={styles.panel}>
          <ExpandedModule
            key={expandedStub.cardId}
            stub={expandedStub}
            deepDive={deepDiveOf(expandedStub)}
            takeFocus={expanded?.fromKeyboard ?? false}
            onClose={collapse}
          />
        </div>
      )}
    </div>
  )
}
